import React, { useState } from 'react'
import {
  AlertCircle,
  Bell,
  Braces,
  Check,
  ClipboardList,
  Copy,
  HelpCircle,
  Shield,
} from 'lucide-react'
import { JsonView, darkStyles } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'
import { Modal } from '../Modal'
import { Text } from '../Text'
import { Button } from '../Button'
import { useT } from '../../../app/lib/i18n'
import {
  linkifyHistorySummary,
  resolveHistoryEntityLinks,
  type HistoryEntityLink,
} from './logSummaryLinks'
import * as s from './LogView.css'

export type { HistoryEntityLink }
export type HistoryLogType = 'audit' | 'security' | 'error' | 'notification' | string

export interface LogViewRow {
  id: string
  occurredAt: string
  type: HistoryLogType
  summary: string
  meta?: Record<string, unknown>
  users?: Array<{ id?: string; displayName?: string }>
  organizationId?: string | null
  /** Origin service label (e.g. "User service"), when known. */
  sourceLabel?: string
}

export interface LogViewProps<TRow extends LogViewRow> {
  rows: TRow[]
  loading?: boolean
  fetching?: boolean
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  emptyAction?: React.ReactNode
  onRowClick?: (row: TRow) => void
  /** Navigate when a linked entity name in the summary is clicked. */
  onEntityClick?: (link: HistoryEntityLink) => void
  formatTime?: (iso: string) => string
  formatFullTime?: (iso: string) => string
}

const TYPE_CLASS: Record<string, string> = {
  audit: s.logTypeAudit,
  security: s.logTypeSecurity,
  error: s.logTypeError,
  notification: s.logTypeNotification,
}

const TYPE_LABEL: Record<string, string> = {
  audit: 'Audit',
  security: 'Security',
  error: 'Error',
  notification: 'Notification',
}

function TypeIcon({ type }: { type: string }) {
  const props = { size: 13, strokeWidth: 2, 'aria-hidden': true as const }
  switch (type) {
    case 'audit':
      return <ClipboardList {...props} />
    case 'security':
      return <Shield {...props} />
    case 'error':
      return <AlertCircle {...props} />
    case 'notification':
      return <Bell {...props} />
    default:
      return <HelpCircle {...props} />
  }
}

function defaultFormatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function defaultFormatFullTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

function MetaJsonModal({
  open,
  setOpen,
  row,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  row: LogViewRow | null
}) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const meta = row?.meta ?? {}

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(meta, null, 2))
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Modal open={open} setOpen={setOpen} minWidth="620px" maxWidth="860px" maxHeight="80vh">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Braces size={18} style={{ color: 'var(--color-fgMuted)' }} />
          <Text level="h3" weight="bold">
            {t('shared.listView.log.metaModalTitle')}
          </Text>
        </div>
        {row?.summary ? (
          <Text level="body-small" style={{ color: 'var(--color-fgMuted)' }}>
            {row.summary}
          </Text>
        ) : null}

        <div
          style={{
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 12,
            lineHeight: 1.6,
            overflowX: 'auto',
            fontFamily: 'monospace',
            maxHeight: '55vh',
            overflowY: 'auto',
          }}
        >
          <JsonView
            data={meta}
            style={darkStyles}
            shouldExpandNode={(level: number) => level < 2}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconLeft={copied ? <Check strokeWidth={2.5} /> : <Copy />}
            onClick={() => void handleCopy()}
          >
            {copied
              ? t('shared.listView.log.copied')
              : t('shared.listView.log.copyJson')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function SummaryContent({
  row,
  onEntityClick,
}: {
  row: LogViewRow
  onEntityClick?: (link: HistoryEntityLink) => void
}) {
  const links = resolveHistoryEntityLinks({
    users: row.users,
    meta: row.meta,
    organizationId: row.organizationId,
  })

  if (!onEntityClick || links.length === 0) {
    return <>{row.summary}</>
  }

  return (
    <>
      {linkifyHistorySummary(row.summary, links, (link, key) => (
        <button
          key={key}
          type="button"
          className={s.logSummaryLink}
          onClick={(e) => {
            e.stopPropagation()
            onEntityClick(link)
          }}
        >
          {link.label}
        </button>
      ))}
    </>
  )
}

export function LogView<TRow extends LogViewRow>({
  rows,
  loading,
  fillContainer,
  emptyTitle = 'No events',
  emptySubtitle,
  emptyAction,
  onRowClick,
  onEntityClick,
  formatTime = defaultFormatTime,
  formatFullTime = defaultFormatFullTime,
}: LogViewProps<TRow>) {
  const t = useT()
  const [metaRow, setMetaRow] = useState<TRow | null>(null)
  const wrapClass = fillContainer ? s.logWrapFill : s.logWrap

  if (loading && rows.length === 0) {
    return (
      <div className={wrapClass}>
        <div className={fillContainer ? s.logScrollWrap : undefined}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={s.logSkeleton} />
          ))}
        </div>
      </div>
    )
  }

  if (!loading && rows.length === 0) {
    return (
      <div className={wrapClass}>
        <div className={s.logEmpty}>
          <div className={s.logEmptyTitle}>{emptyTitle}</div>
          {emptySubtitle ? <div>{emptySubtitle}</div> : null}
          {emptyAction ? <div className={s.logEmptyAction}>{emptyAction}</div> : null}
        </div>
      </div>
    )
  }

  const list = (
    <div className={s.logList} role="list">
      {rows.map((row) => {
        const typeClass = TYPE_CLASS[row.type] ?? ''
        const typeLabel = TYPE_LABEL[row.type] ?? row.type
        return (
          <div
            key={row.id}
            role="listitem"
            className={`${s.logRow}${onRowClick ? ` ${s.logRowClickable}` : ''}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(row)
                    }
                  }
                : undefined
            }
            tabIndex={onRowClick ? 0 : undefined}
          >
            <span
              className={`${s.logTypeIcon} ${typeClass}`}
              title={typeLabel}
              aria-label={typeLabel}
            >
              <TypeIcon type={row.type} />
            </span>
            <span className={s.logTime} title={formatFullTime(row.occurredAt)}>
              {formatTime(row.occurredAt)}
            </span>
            <span className={s.logSummary} title={row.summary}>
              {row.sourceLabel ? (
                <span className={s.logSourceChip}>{row.sourceLabel}</span>
              ) : null}
              <span className={s.logSummaryText}>
                <SummaryContent row={row} onEntityClick={onEntityClick} />
              </span>
            </span>
            <button
              type="button"
              className={s.logMetaBtn}
              title={t('shared.listView.log.viewMeta')}
              aria-label={t('shared.listView.log.viewMeta')}
              onClick={(e) => {
                e.stopPropagation()
                setMetaRow(row)
              }}
            >
              <Braces size={14} strokeWidth={2} aria-hidden />
            </button>
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      {fillContainer ? (
        <div className={wrapClass}>
          <div className={s.logScrollWrap}>{list}</div>
        </div>
      ) : (
        <div className={wrapClass}>{list}</div>
      )}
      <MetaJsonModal
        open={metaRow != null}
        setOpen={(open) => {
          if (!open) setMetaRow(null)
        }}
        row={metaRow}
      />
    </>
  )
}
